// Google Analytics 4 utility with user-wise tracking for Admin Web
// Measurement ID from Firebase config: G-YZB11MV97E

const GA_MEASUREMENT_ID =
    process.env.REACT_APP_GA_MEASUREMENT_ID ||
    process.env.REACT_APP_FIREBASE_MEASUREMENT_ID ||
    "G-YZB11MV97E";

declare global {
    interface Window {
        dataLayer: any[];
        gtag?: (...args: any[]) => void;
    }
}

// Initialize gtag
export const initGA = (): void => {
    if (typeof window === "undefined") return;
    if (window.gtag) return;

    const script1 = document.createElement("script");
    script1.async = true;
    script1.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
    document.head.appendChild(script1);

    window.dataLayer = window.dataLayer || [];
    window.gtag = function gtag() {
        window.dataLayer.push(arguments);
    };
    window.gtag("js", new Date());
    window.gtag("config", GA_MEASUREMENT_ID, {
        send_page_view: false,
        cookie_flags: "SameSite=None;Secure",
    });
};

// Set user properties for user-wise tracking
export const setAnalyticsUser = (userData: {
    id?: string | number;
    email?: string;
    fullName?: string;
    userType?: string;
}): void => {
    if (!window.gtag) return;

    const userId = String(userData.id || "");
    if (userId) {
        window.gtag("config", GA_MEASUREMENT_ID, {
            user_id: userId,
        });
        window.gtag("set", "user_properties", {
            user_type: "admin",
            platform: "admin_web",
            user_id_custom: userId,
            admin_user_type: userData.userType || "ADMIN",
        });
    }
};

// Clear user on logout
export const clearAnalyticsUser = (): void => {
    if (!window.gtag) return;
    window.gtag("config", GA_MEASUREMENT_ID, {
        user_id: undefined,
    });
};

// Track page views
export const trackPageView = (url: string, title?: string): void => {
    if (!window.gtag) return;
    window.gtag("event", "page_view", {
        page_path: url,
        page_title: title || document.title,
        page_location: window.location.href,
        platform: "admin_web",
    });
};

// Track custom events
export const trackEvent = (
    eventName: string,
    params: Record<string, any> = {}
): void => {
    if (!window.gtag) return;
    window.gtag("event", eventName, {
        ...params,
        platform: "admin_web",
        timestamp: new Date().toISOString(),
    });
};

// Pre-defined event trackers for admin actions
export const analyticsEvents = {
    // Auth
    login: () => trackEvent("login", { method: "email", platform: "admin_web" }),
    logout: () => trackEvent("logout", { platform: "admin_web" }),

    // Admin actions
    notificationSent: (recipientType: string, notificationType: string, count: number) =>
        trackEvent("admin_notification_sent", { recipient_type: recipientType, notification_type: notificationType, recipient_count: count }),

    // Clinician management
    clinicianApproved: (clinicianId: string | number) =>
        trackEvent("clinician_approved", { clinician_id: clinicianId }),
    clinicianRejected: (clinicianId: string | number) =>
        trackEvent("clinician_rejected", { clinician_id: clinicianId }),
    clinicianViewed: (clinicianId: string | number) =>
        trackEvent("clinician_viewed", { clinician_id: clinicianId }),

    // Session management
    sessionViewed: (sessionId: string | number) =>
        trackEvent("session_viewed", { session_id: sessionId }),

    // Patient management
    patientViewed: (patientId: string | number) =>
        trackEvent("patient_viewed", { patient_id: patientId }),

    // Payouts
    payoutProcessed: (payoutId: string | number, amount: number) =>
        trackEvent("payout_processed", { payout_id: payoutId, value: amount, currency: "SAR" }),

    // Content management
    resourceCreated: (type: string) => trackEvent("resource_created", { resource_type: type }),
    blogPublished: (blogId: string | number) => trackEvent("blog_published", { blog_id: blogId }),
    serviceUpdated: (serviceId: string | number) => trackEvent("service_updated", { service_id: serviceId }),

    // Settings
    settingsUpdated: (section: string) => trackEvent("settings_updated", { section }),
    roleCreated: (roleName: string) => trackEvent("role_created", { role_name: roleName }),

    // Dashboard
    dashboardViewed: () => trackEvent("dashboard_viewed"),
    analyticsViewed: () => trackEvent("analytics_dashboard_viewed"),
};
